import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Plus, Loader2, Search, GitBranch, FolderGit2, ChevronDown, ChevronRight,
  Folder, Check, MoreVertical, RefreshCw, Trash2, ShieldCheck, AlertCircle,
} from 'lucide-react'
import { guardianService, type GuardianApp, type GuardianRepo, type GuardianPortfolioHealth } from '@/services/guardian'
import { GuardianAppDetail } from './guardian-app-detail'

const frameworkConfig: Record<string, { color: string; bgColor: string }> = {
  'react-native': { color: 'text-sky-700', bgColor: 'bg-sky-50' },
  flutter: { color: 'text-indigo-700', bgColor: 'bg-indigo-50' },
  godot: { color: 'text-violet-700', bgColor: 'bg-violet-50' },
  unity: { color: 'text-green-700', bgColor: 'bg-green-50' },
  node: { color: 'text-emerald-700', bgColor: 'bg-emerald-50' },
  python: { color: 'text-yellow-700', bgColor: 'bg-yellow-50' },
}

const actionLabels: Record<string, string> = {
  'scan.completed': 'Scan completed',
  'scan.triggered': 'Scan triggered',
  'qa.completed': 'QA completed',
  'qa.bundle_triggered': 'QA triggered',
  'pr.created': 'PR created',
  'pr.triggered': 'PR triggered',
  'pr.merged': 'PR merged',
  'pr.declined': 'PR declined',
  'app.added': 'App added',
  'app.settings_changed': 'Settings changed',
}

function timeAgo(date: string | null): string {
  if (!date) return '—'
  const d = new Date(date)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return d.toLocaleDateString()
}

function getHealthColor(score: number | undefined): string {
  if (score === undefined) return 'text-slate-400'
  if (score >= 80) return 'text-emerald-600'
  if (score >= 60) return 'text-amber-600'
  return 'text-red-600'
}

function getHealthBg(score: number | undefined): string {
  if (score === undefined) return 'bg-slate-50'
  if (score >= 80) return 'bg-emerald-50'
  if (score >= 60) return 'bg-amber-50'
  return 'bg-red-50'
}

interface GuardianAppsTabProps {
  health: GuardianPortfolioHealth | null
}

export function GuardianAppsTab({ health }: GuardianAppsTabProps) {
  const [apps, setApps] = useState<GuardianApp[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [dialogStep, setDialogStep] = useState<'provider' | 'confirm' | 'wrong-account' | 'repos'>('provider')

  // OAuth
  const [oauthUser, setOauthUser] = useState<any>(null)
  const [repos, setRepos] = useState<GuardianRepo[]>([])
  const [loadingRepos, setLoadingRepos] = useState(false)
  const [searchFilter, setSearchFilter] = useState('')
  const [selectedRepos, setSelectedRepos] = useState<Set<string>>(new Set())
  const [addingBulk, setAddingBulk] = useState(false)
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set())
  const [expandedApp, setExpandedApp] = useState<string | null>(null)
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set())
  const [scanning, setScanning] = useState(false)

  useEffect(() => { loadApps() }, [])

  async function loadApps() {
    setLoading(true)
    try {
      setApps(await guardianService.getApps())
    }
    catch (e) { console.error('Failed to load apps:', e) }
    finally { setLoading(false) }
  }

  // --- OAuth + Add App Flow ---
  function handleAddApp() {
    setDialogStep('provider')
    setShowAddDialog(true)
    setRepos([])
    setSearchFilter('')
    setSelectedRepos(new Set())
    setExpandedProjects(new Set())
    setOauthUser(null)
  }

  async function handleConnectProvider(provider: string) {
    try {
      const { oauth_url } = await guardianService.getOAuthUrl(provider)
      const w = 700, h = 500
      const left = window.screenX + (window.outerWidth - w) / 2
      const top = window.screenY + (window.outerHeight - h) / 2
      const popup = window.open(oauth_url, 'guardian-oauth', `width=${w},height=${h},left=${left},top=${top},scrollbars=yes`)

      function handleMessage(event: MessageEvent) {
        if (event.data?.source === 'guardian-oauth' && event.data?.success) {
          window.removeEventListener('message', handleMessage)
          loadUserInfo(provider)
        }
      }
      window.addEventListener('message', handleMessage)
      const interval = setInterval(() => { if (popup?.closed) clearInterval(interval) }, 500)
    } catch (e) { console.error('OAuth failed:', e) }
  }

  async function loadUserInfo(provider: string) {
    try {
      const info = await guardianService.getUserInfo(provider)
      setOauthUser(info)
      setDialogStep('confirm')
    } catch {
      setDialogStep('repos')
      loadRepos()
    }
  }

  async function loadRepos() {
    setLoadingRepos(true)
    setRepos([])
    setSelectedRepos(new Set())
    try { setRepos(await guardianService.getRepos('bitbucket')) }
    catch (e) { console.error('Failed to load repos:', e) }
    finally { setLoadingRepos(false) }
  }

  function toggleRepo(slug: string) {
    setSelectedRepos(prev => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug); else next.add(slug)
      return next
    })
  }

  function toggleProject(projectRepos: GuardianRepo[]) {
    const registeredSlugs = new Set(apps.map(a => a.app_slug))
    setSelectedRepos(prev => {
      const next = new Set(prev)
      const slugs = projectRepos.filter(r => !registeredSlugs.has(r.slug)).map(r => r.slug)
      const allSelected = slugs.every(s => next.has(s))
      if (allSelected) slugs.forEach(s => next.delete(s))
      else slugs.forEach(s => next.add(s))
      return next
    })
  }

  async function handleAddSelected() {
    if (selectedRepos.size === 0) return
    setAddingBulk(true)
    for (const repo of repos.filter(r => selectedRepos.has(r.slug))) {
      try {
        const appName = repo.name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
        await guardianService.registerApp({
          app_name: appName, app_slug: repo.slug, repo_url: repo.ssh_url,
          git_provider: 'bitbucket', repo_branch: repo.default_branch,
        })
      } catch (e) { console.error(`Failed to add ${repo.slug}:`, e) }
    }
    await loadApps()
    setSelectedRepos(new Set())
    setAddingBulk(false)
    setShowAddDialog(false)
  }

  async function handleRemoveApp(slug: string) {
    if (!confirm(`Remove "${slug}" from Guardian? This deletes all scan data for this app.`)) return
    try {
      await guardianService.deactivateApp(slug)
      await loadApps()
    } catch (e) { console.error('Failed to remove:', e) }
  }

  async function handleScanApp(slug: string) {
    try { await guardianService.triggerAppScan(slug) }
    catch (e) { console.error('Failed to scan:', e) }
  }

  async function handleScanSelected() {
    if (selectedApps.size === 0) return
    setScanning(true)
    try {
      await guardianService.triggerScanSelected(Array.from(selectedApps))
      setTimeout(() => setScanning(false), 5000)
    } catch (e) {
      console.error('Failed to trigger scan:', e)
      setScanning(false)
    }
  }

  function toggleAppSelection(slug: string) {
    setSelectedApps(prev => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug); else next.add(slug)
      return next
    })
  }

  function toggleAllApps() {
    if (selectedApps.size === apps.length) {
      setSelectedApps(new Set())
    } else {
      setSelectedApps(new Set(apps.map(a => a.app_slug)))
    }
  }

  const registeredSlugs = new Set(apps.map(a => a.app_slug))
  const filteredRepos = repos.filter(r => {
    if (!searchFilter) return true
    const q = searchFilter.toLowerCase()
    return r.name.toLowerCase().includes(q) || r.project?.toLowerCase().includes(q)
  })

  // --- Render ---
  if (loading) {
    return (
      <div className="space-y-2">
        {[1,2,3].map(i => <Skeleton key={i} className="h-16 rounded-lg" />)}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Empty state */}
      {apps.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <div className="text-center space-y-6">
            <div className="mx-auto h-28 w-28 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
              <ShieldCheck className="h-14 w-14 text-blue-600" />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-semibold text-slate-900">No apps monitored yet</h3>
              <p className="text-slate-500 max-w-md mx-auto">
                Connect your git provider and select repositories to start monitoring dependencies.
              </p>
            </div>
            <Button onClick={handleAddApp} size="lg" className="gap-2">
              <Plus className="h-5 w-5" />
              Add Your First App
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Action buttons */}
          <div className="flex items-center justify-between">
            <Button onClick={handleScanSelected} variant="outline" size="sm" className="gap-1.5" disabled={scanning || selectedApps.size === 0}>
              <RefreshCw className={`h-4 w-4 ${scanning ? 'animate-spin' : ''}`} />
              {scanning ? 'Scanning...' : `Scan ${selectedApps.size} Selected`}
            </Button>
            <Button onClick={handleAddApp} size="sm" className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add App
            </Button>
          </div>

          {/* App List */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50/50">
                  <th className="w-10 px-3 py-3">
                    <Checkbox
                      checked={apps.length > 0 && selectedApps.size === apps.length}
                      onCheckedChange={toggleAllApps}
                    />
                  </th>
                  <th className="text-left font-medium text-slate-500 px-4 py-3 w-12">Health</th>
                  <th className="text-left font-medium text-slate-500 px-4 py-3">Application</th>
                  <th className="text-left font-medium text-slate-500 px-4 py-3 w-28">Framework</th>
                  <th className="text-left font-medium text-slate-500 px-4 py-3 w-28">Dependencies</th>
                  <th className="text-left font-medium text-slate-500 px-4 py-3 w-36">Findings</th>
                  <th className="text-left font-medium text-slate-500 px-4 py-3 w-28">Status</th>
                  <th className="text-left font-medium text-slate-500 px-4 py-3 w-24">Last Scan</th>
                  <th className="text-left font-medium text-slate-500 px-4 py-3 w-28">Last Action</th>
                  <th className="w-10 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {apps.map(app => {
                  const score = health?.app_scores?.[app.app_slug]
                  const fw = frameworkConfig[app.framework] || { color: 'text-slate-600', bgColor: 'bg-slate-50' }
                  const isExpanded = expandedApp === app.app_slug
                  const hasScanned = app.last_scan_at != null

                  return (
                    <React.Fragment key={app.id}>
                      <tr
                        className={`group border-b hover:bg-slate-50/80 transition-colors cursor-pointer ${isExpanded ? 'bg-slate-50' : ''}`}
                        onClick={() => setExpandedApp(isExpanded ? null : app.app_slug)}
                      >
                        {/* Checkbox */}
                        <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedApps.has(app.app_slug)}
                            onCheckedChange={() => toggleAppSelection(app.app_slug)}
                          />
                        </td>

                        {/* Health Score */}
                        <td className="px-4 py-3">
                          <div className={`h-9 w-9 rounded-lg ${getHealthBg(score)} flex items-center justify-center`}>
                            <span className={`text-sm font-bold ${getHealthColor(score)}`}>
                              {score !== undefined ? score : '—'}
                            </span>
                          </div>
                        </td>

                        {/* App Name + Repo */}
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-900">{app.app_name}</p>
                          <p className="text-xs text-slate-400 font-mono truncate max-w-xs">{app.repo_url}</p>
                        </td>

                        {/* Framework */}
                        <td className="px-4 py-3">
                          <Badge variant="secondary" className={`${fw.bgColor} ${fw.color} border-0 text-xs font-medium`}>
                            {app.framework === 'unknown' ? 'Pending' : app.framework}
                          </Badge>
                        </td>

                        {/* Deps */}
                        <td className="px-4 py-3 text-slate-600">
                          {hasScanned ? app.dep_count : '—'}
                        </td>

                        {/* Findings */}
                        <td className="px-4 py-3">
                          {hasScanned ? (
                            (app.findings_high + app.findings_medium + app.findings_low) > 0 ? (
                              <div className="flex items-center gap-2 flex-wrap">
                                {app.findings_high > 0 && <span className="text-xs font-medium text-red-600">{app.findings_high} high</span>}
                                {app.findings_medium > 0 && <span className="text-xs font-medium text-amber-600">{app.findings_medium} med</span>}
                                {app.findings_low > 0 && <span className="text-xs text-blue-600">{app.findings_low} low</span>}
                              </div>
                            ) : (
                              <span className="text-xs text-emerald-600 font-medium">No issues</span>
                            )
                          ) : (
                            <span className="text-xs text-slate-400">Not scanned</span>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">
                          {hasScanned ? (
                            app.findings_in_pr > 0 ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                                <GitBranch className="h-3 w-3" />
                                {app.findings_in_pr} in PR
                              </span>
                            ) : (app.findings_high + app.findings_medium + app.findings_low) > 0 ? (
                              <span className="text-xs text-amber-600 font-medium">Needs attention</span>
                            ) : (
                              <span className="text-xs text-emerald-600 font-medium">Healthy</span>
                            )
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>

                        {/* Last Scan */}
                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-500">{timeAgo(app.last_scan_at)}</span>
                        </td>

                        {/* Last Action */}
                        <td className="px-4 py-3">
                          {app.last_action_at ? (
                            <div>
                              <span className="text-xs text-slate-600 block truncate max-w-[140px]">{actionLabels[app.last_action_type || ''] || app.last_action_type || 'Action'}</span>
                              <span className="text-[10px] text-slate-400">{app.last_action_actor || 'system'} - {timeAgo(app.last_action_at)}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>

                        {/* Settings */}
                        <td className="px-2 py-3" onClick={e => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7">
                                <MoreVertical className="h-4 w-4 text-slate-400" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuItem onClick={() => handleScanApp(app.app_slug)}>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Scan Now
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <div className="px-2 py-1.5">
                                <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1.5">Scan Frequency</p>
                                <div className="flex gap-1">
                                  {['daily', 'weekly', 'manual'].map(freq => (
                                    <button key={freq}
                                      className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
                                        (app.scan_frequency || 'daily') === freq
                                          ? 'bg-blue-50 border-blue-200 text-blue-700'
                                          : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                                      }`}
                                      onClick={() => {
                                        guardianService.updateAppSettings(app.app_slug, { scan_frequency: freq })
                                        setApps(prev => prev.map(a => a.app_slug === app.app_slug ? { ...a, scan_frequency: freq } : a))
                                      }}
                                    >{freq}</button>
                                  ))}
                                </div>
                              </div>
                              <div className="px-2 py-1.5">
                                <div className="flex items-center justify-between">
                                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Auto-merge</p>
                                  <Switch
                                    checked={!!app.auto_merge_enabled}
                                    onCheckedChange={(v) => {
                                      guardianService.updateAppSettings(app.app_slug, { auto_merge_enabled: v })
                                      setApps(prev => prev.map(a => a.app_slug === app.app_slug ? { ...a, auto_merge_enabled: v } : a))
                                    }}
                                  />
                                </div>
                              </div>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-red-600" onClick={() => handleRemoveApp(app.app_slug)}>
                                <Trash2 className="h-4 w-4 mr-2" />
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={11} className="p-0">
                            <GuardianAppDetail
                              appSlug={app.app_slug}
                              appName={app.app_name}
                              onClose={() => setExpandedApp(null)}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>

        </>
      )}

      {/* Add App Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>
              {dialogStep === 'provider' ? 'Connect Git Provider' :
               dialogStep === 'confirm' ? 'Confirm Account' :
               dialogStep === 'wrong-account' ? 'Switch Account' :
               'Select Repositories'}
            </DialogTitle>
            <DialogDescription>
              {dialogStep === 'provider' ? 'Authenticate with your git provider to browse repositories.' :
               dialogStep === 'confirm' ? 'Verify you are connected with the correct account.' :
               dialogStep === 'wrong-account' ? 'Follow the steps below to connect with a different account.' :
               'Select repositories to monitor. Only selected repositories will be accessed.'}
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
            {/* Step: Provider selection */}
            {dialogStep === 'provider' && (
              <div className="space-y-2">
                <button
                  className="flex items-center gap-4 w-full p-4 rounded-xl border hover:bg-slate-50 transition-colors text-left"
                  onClick={() => handleConnectProvider('bitbucket')}
                >
                  <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">B</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Bitbucket</p>
                    <p className="text-xs text-slate-500">Connect to browse and add repositories</p>
                  </div>
                </button>
                {['GitHub', 'GitLab'].map(name => (
                  <div key={name} className="flex items-center gap-4 p-4 rounded-xl border opacity-50 cursor-not-allowed">
                    <div className="h-10 w-10 rounded-lg bg-slate-300 flex items-center justify-center">
                      <span className="text-white font-bold text-sm">{name[0]}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{name} <Badge variant="outline" className="ml-2 text-xs text-slate-400">Coming soon</Badge></p>
                      <p className="text-xs text-slate-500">Connect to browse and add repositories</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Step: Confirm account */}
            {dialogStep === 'confirm' && oauthUser && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-xl border bg-slate-50">
                  {oauthUser.avatar_url && <img src={oauthUser.avatar_url} alt="" className="w-12 h-12 rounded-full" />}
                  <div>
                    <p className="font-semibold text-slate-900">{oauthUser.display_name}</p>
                    <p className="text-sm text-slate-500">@{oauthUser.username}</p>
                    {oauthUser.workspaces?.length > 0 && (
                      <p className="text-xs text-slate-400 mt-1">Workspaces: {oauthUser.workspaces.map((w: any) => w.name).join(', ')}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step: Wrong account */}
            {dialogStep === 'wrong-account' && (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 space-y-3">
                <p className="text-sm font-medium text-amber-800">To connect with a different account:</p>
                <ol className="text-sm text-amber-700 space-y-2 list-decimal list-inside">
                  <li>Open <a href="https://bitbucket.org/account/settings/" target="_blank" rel="noopener" className="text-blue-600 underline">Bitbucket Settings</a> in a new tab</li>
                  <li>Log out of the current account</li>
                  <li>Log in with the correct account</li>
                  <li>Come back here and click "Try again"</li>
                </ol>
              </div>
            )}

            {/* Step: Repo picker */}
            {dialogStep === 'repos' && (
              <>
                <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 mb-4 text-sm">
                  <ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
                  <span className="text-emerald-800">Only the repositories you select below will be accessible by Guardian.</span>
                </div>

                {loadingRepos ? (
                  <div className="flex items-center justify-center py-12 gap-2 text-slate-500">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Loading repositories...
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input placeholder="Search repositories..." value={searchFilter} onChange={e => setSearchFilter(e.target.value)} className="pl-9" />
                    </div>

                    <div className="max-h-[40vh] overflow-y-auto space-y-1 pr-1">
                      {filteredRepos.length === 0 ? (
                        <p className="text-center py-8 text-slate-500 text-sm">
                          {searchFilter ? 'No repositories match your search.' : 'All repositories are already added.'}
                        </p>
                      ) : (() => {
                        const grouped: Record<string, typeof filteredRepos> = {}
                        for (const repo of filteredRepos) {
                          const p = repo.project || 'Other'
                          if (!grouped[p]) grouped[p] = []
                          grouped[p].push(repo)
                        }
                        const projects = Object.keys(grouped).sort((a, b) => a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b))
                        const isSearching = searchFilter.length > 0

                        return projects.map(projectName => {
                          const projectRepos = grouped[projectName]
                          const availableRepos = projectRepos.filter(r => !registeredSlugs.has(r.slug))
                          const isExpanded = isSearching || expandedProjects.has(projectName)
                          const allSelected = availableRepos.length > 0 && availableRepos.every(r => selectedRepos.has(r.slug))
                          const someSelected = availableRepos.some(r => selectedRepos.has(r.slug))

                          return (
                            <div key={projectName}>
                              <div className="flex items-center gap-1.5">
                                {availableRepos.length > 0 && (
                                  <input type="checkbox" checked={allSelected}
                                    ref={el => { if (el) el.indeterminate = someSelected && !allSelected }}
                                    onChange={() => toggleProject(projectRepos)}
                                    className="h-4 w-4 rounded border-slate-300 accent-blue-600 cursor-pointer" />
                                )}
                                <button
                                  className="flex items-center gap-2 flex-1 px-2 py-2 text-sm font-medium hover:bg-slate-50 rounded-lg transition-colors"
                                  onClick={() => {
                                    const next = new Set(expandedProjects)
                                    if (next.has(projectName)) next.delete(projectName); else next.add(projectName)
                                    setExpandedProjects(next)
                                  }}
                                >
                                  {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                                  <Folder className="h-4 w-4 text-slate-400" />
                                  <span className="text-slate-700">{projectName}</span>
                                  <span className="text-xs text-slate-400">({projectRepos.length})</span>
                                </button>
                              </div>
                              {isExpanded && (
                                <div className="ml-7 space-y-1 mt-1 mb-2">
                                  {projectRepos.map(repo => {
                                    const isAdded = registeredSlugs.has(repo.slug)
                                    return (
                                      <label key={repo.full_name}
                                        className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${
                                          isAdded ? 'opacity-50 cursor-default bg-slate-50' : 'hover:bg-slate-50 cursor-pointer'
                                        }`}>
                                        {isAdded ? (
                                          <Check className="h-4 w-4 text-emerald-500 shrink-0" />
                                        ) : (
                                          <input type="checkbox" checked={selectedRepos.has(repo.slug)} onChange={() => toggleRepo(repo.slug)}
                                            className="h-4 w-4 rounded border-slate-300 accent-blue-600" />
                                        )}
                                        <FolderGit2 className="h-4 w-4 text-slate-400 shrink-0" />
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2">
                                            <p className="font-medium text-sm text-slate-900 truncate">{repo.name}</p>
                                            {isAdded && <Badge variant="outline" className="text-[10px] px-1.5 text-emerald-600 border-emerald-200">Added</Badge>}
                                          </div>
                                          <div className="flex items-center gap-2 text-xs text-slate-400">
                                            <GitBranch className="h-3 w-3" />{repo.default_branch}
                                            {repo.language && <span>{repo.language}</span>}
                                          </div>
                                        </div>
                                      </label>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                          )
                        })
                      })()}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <DialogFooter className="px-6 py-4 border-t">
            {dialogStep === 'confirm' && (
              <div className="flex items-center justify-between w-full">
                <Button variant="outline" onClick={() => setDialogStep('wrong-account')}>Wrong account</Button>
                <Button onClick={() => { setDialogStep('repos'); loadRepos() }}>Continue with this account</Button>
              </div>
            )}
            {dialogStep === 'wrong-account' && (
              <div className="flex items-center justify-between w-full">
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button onClick={() => { setDialogStep('provider'); setOauthUser(null) }}>Try again</Button>
              </div>
            )}
            {dialogStep === 'repos' && (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm text-slate-500">{selectedRepos.size > 0 ? `${selectedRepos.size} selected` : ''}</span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>Done</Button>
                  {selectedRepos.size > 0 && (
                    <Button onClick={handleAddSelected} disabled={addingBulk}>
                      {addingBulk ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Adding...</> : `Add ${selectedRepos.size} App${selectedRepos.size > 1 ? 's' : ''}`}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
